import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { styles } from '../../../../styles/themes/style';
import { campaignMobile } from '../../utils/campaignBreakpoints';

/**
 * The language picker that sits to the right of the compose actions.
 *
 * Lifted out of CampaignSequenceBuilder so the email composer and the LinkedIn
 * steps show the same control rather than two that drift apart.
 */

const ACCENT = styles.colors.primary600;
const BORDER = '#e2e8f0';
const INK    = '#334155';

const BASE_LANGUAGES: { code: string; flag: string; label?: string }[] = [
  { code: 'English',    flag: '🇺🇸' },
  { code: 'Spanish',    flag: '🇪🇸' },
  { code: 'French',     flag: '🇫🇷' },
  { code: 'German',     flag: '🇩🇪' },
  { code: 'Portuguese', flag: '🇧🇷' },
  { code: 'Italian',    flag: '🇮🇹' },
  { code: 'Dutch',      flag: '🇳🇱' },
  { code: 'Polish',     flag: '🇵🇱' },
  { code: 'Russian',    flag: '🇷🇺' },
  { code: 'Swedish',    flag: '🇸🇪' },
  { code: 'Norwegian',  flag: '🇳🇴' },
  { code: 'Danish',     flag: '🇩🇰' },
  { code: 'Finnish',    flag: '🇫🇮' },
  { code: 'Croatian',   flag: '🇭🇷' },
  { code: 'Serbian',    flag: '🇷🇸' },
  { code: 'Czech',      flag: '🇨🇿' },
  { code: 'Turkish',    flag: '🇹🇷' },
  { code: 'Arabic',     flag: '🇸🇦' },
  { code: 'Chinese',    flag: '🇨🇳' },
  { code: 'Japanese',   flag: '🇯🇵' },
  { code: 'Korean',     flag: '🇰🇷' },
];

export type LanguageStepType = 'message' | 'sn_message' | 'post_comment' | 'connection_request' | 'email';

const AUTO_LABEL: Record<LanguageStepType, string> = {
  message: 'Auto (English)',
  sn_message: 'Auto (English)',
  post_comment: 'Auto (post language)',
  connection_request: 'Auto (English)',
  email: 'Auto (English)',
};

export function languagesForStep(stepType: LanguageStepType) {
  return [
    { code: 'Auto', flag: '🌐', label: AUTO_LABEL[stepType] },
    ...BASE_LANGUAGES,
  ];
}

interface LangDropProps {
  value: string;
  onChange: (lang: string) => void;
  stepType: LanguageStepType;
}

const CampaignLanguageDropdown: React.FC<LangDropProps> = ({ value, onChange, stepType }) => {
  const languages = React.useMemo(() => languagesForStep(stepType), [stepType]);
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState<{ top: number; left: number; place: 'above' | 'below' }>({
    top: 0, left: 0, place: 'above',
  });
  const menuRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const selected = languages.find(l => l.code === value) ?? languages[0];

  const calcPos = React.useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const menuH = 280;
    const gap = 8;
    const spaceAbove = r.top - gap;
    const spaceBelow = window.innerHeight - r.bottom - gap;
    // Prefer below; flip above when there isn't enough room under the trigger.
    const place: 'above' | 'below' =
      spaceBelow < menuH && spaceAbove > spaceBelow ? 'above' : 'below';
    const left = Math.max(8, Math.min(r.right - 200, window.innerWidth - 208));
    setPos({
      top: place === 'below' ? r.bottom + gap : r.top - gap,
      left,
      place,
    });
  }, []);
  React.useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  // The menu is fixed-positioned in a body portal — keep it anchored to the
  // trigger while the page (or any nested scroll container) scrolls or resizes.
  React.useEffect(() => {
    if (!open) return;
    calcPos();
    const reposition = () => calcPos();
    window.addEventListener('scroll', reposition, true); // capture: catches nested scrollers
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, calcPos]);

  const handleToggle = () => {
    calcPos();
    setOpen(o => !o);
  };

  return (
    <LangWrap>
      <LangTrigger ref={triggerRef} type="button" $open={open} onClick={handleToggle}>
        <LangIconWrap>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z"/>
          </svg>
        </LangIconWrap>
        <LangTriggerLabel>Language</LangTriggerLabel>
        <LangDivider />
        <LangTriggerVal>{selected.label ?? selected.code}</LangTriggerVal>
        <LangChevron $open={open}>
          <svg width="8" height="5" viewBox="0 0 8 5" fill="none">
            <path d="M1 1l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </LangChevron>
      </LangTrigger>

      {open && createPortal(
        <LangMenu ref={menuRef} $place={pos.place} style={{ top: pos.top, left: pos.left }}>
          {languages.map(l => (
            <LangItem key={l.code} $active={l.code === value}
              onClick={() => { onChange(l.code); setOpen(false); }}>
              <LangFlag>{l.flag}</LangFlag>
              <span>{l.label ?? l.code}</span>
              {l.code === value && (
                <LangCheck>
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </LangCheck>
              )}
            </LangItem>
          ))}
        </LangMenu>,
        document.body
      )}
    </LangWrap>
  );
};

export default CampaignLanguageDropdown;

// ─── Styles ───────────────────────────────────────────────────────────────────

const LangWrap = styled.div`
  flex-shrink: 0;
  ${campaignMobile`width: 100%;`}
`;

const LangTrigger = styled.button<{ $open: boolean }>`
  display: inline-flex; align-items: center; gap: 6px;
  height: 30px; padding: 0 9px 0 8px; border-radius: 8px; cursor: pointer;
  border: 1.5px solid ${({ $open }) => $open ? ACCENT : BORDER};
  background: ${({ $open }) => $open ? '#f0f4ff' : '#fff'};
  color: ${({ $open }) => $open ? ACCENT : '#475569'};
  transition: all 0.12s;
  &:hover { border-color: ${ACCENT}; color: ${ACCENT}; background: #f8f9ff;
    .lang-globe .path { stroke: ${ACCENT}; } }
  ${campaignMobile`
    width: 100%;
    justify-content: space-between;
    height: 32px;
    box-sizing: border-box;
  `}
`;

const LangIconWrap = styled.span.attrs({ className: 'lang-globe' })`
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  width: 20px; height: 20px; border-radius: 6px;
  background: ${styles.colors.primary100};
  svg { display: block; width: 12px; height: 12px; }
  .path { stroke: ${ACCENT}; fill: none; }
`;

const LangTriggerLabel = styled.span`
  font-family: NunitoSans; font-size: 10px; font-weight: 800;
  text-transform: uppercase; letter-spacing: 0.06em; white-space: nowrap;
`;

const LangDivider = styled.span`
  width: 1px; height: 12px; background: ${BORDER}; flex-shrink: 0;
`;

const LangFlag = styled.span`
  display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
  min-width: 22px; font-size: 15px; line-height: 1;
`;

const LangTriggerVal = styled.span`
  font-family: NunitoSans; font-size: 11px; font-weight: 700;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  max-width: 120px;
  ${campaignMobile`max-width: none; flex: 1; text-align: right;`}
`;

const LangChevron = styled.span<{ $open: boolean }>`
  display: flex; align-items: center; flex-shrink: 0;
  transform: ${({ $open }) => $open ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.15s;
  svg { display: block; }
`;

const LangMenu = styled.div<{ $place: 'above' | 'below' }>`
  position: fixed; z-index: 9999;
  transform: ${({ $place }) => ($place === 'above' ? 'translateY(-100%)' : 'none')};
  width: 200px; max-height: 280px; overflow-y: auto;
  background: #fff; border: 1px solid ${BORDER}; border-radius: 12px;
  box-shadow: ${({ $place }) => (
    $place === 'above'
      ? '0 -8px 30px rgba(20,30,80,0.12), 0 2px 8px rgba(20,30,80,0.06)'
      : '0 8px 30px rgba(20,30,80,0.12), 0 2px 8px rgba(20,30,80,0.06)'
  )};
  padding: 6px;
  scrollbar-width: thin; scrollbar-color: #e2e8f0 transparent;
  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
`;
const LangItem = styled.div<{ $active: boolean }>`
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px; border-radius: 8px; cursor: pointer;
  background: ${({ $active }) => $active ? '#f0f4ff' : 'transparent'};
  font-family: NunitoSans; font-size: 13px; font-weight: ${({ $active }) => $active ? 700 : 500};
  color: ${({ $active }) => $active ? ACCENT : INK};
  white-space: nowrap;
  transition: background 0.1s;
  & > span:nth-child(2) { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; }
  &:hover { background: ${({ $active }) => $active ? '#e8edff' : '#f6f8fc'}; }
`;

const LangCheck = styled.span`
  flex-shrink: 0; display: flex; align-items: center; color: ${ACCENT};
  svg { display: block; }
`;
